import React, { type ReactNode, useMemo, useRef, useState } from 'react';
import { Chip } from '@jotpsych/ui';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import {
  Box,
  Checkbox,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';

import { formatLastUsed } from './catalogLogic';
import type {
  CatalogLabels,
  CatalogRow,
  CatalogSortKey,
  CatalogSortState,
} from './ResourceCatalog.types';

export interface CatalogTableProps {
  /** Visible rows — already filtered and sorted by the container. */
  rows: CatalogRow[];
  labels: CatalogLabels;
  sort: CatalogSortState;
  onSortChange: (sort: CatalogSortState) => void;
  selectedIds: string[];
  onToggleRow: (id: string) => void;
  /** Select all visible rows, or clear when all are already selected. */
  onToggleAll: () => void;
  /** Row-level archive/restore (the trailing icon action). */
  onRowAction: (row: CatalogRow) => void;
  /** True while a verb is in flight — disables row actions. */
  pending: boolean;
  loading: boolean;
  renderDetail?: (row: CatalogRow) => ReactNode;
  /** Rendered as a full-width row when there are no rows and not loading. */
  emptyState?: ReactNode;
  /** Fired when a detail row is expanded or collapsed — tracking seam. */
  onExpansionChange?: (id: string, expanded: boolean) => void;
}

const SKELETON_ROW_COUNT = 6;

/**
 * Above this row count the body is virtualized (TanStack Virtual) inside a
 * capped-height scroll container — the pilot orgs render 268–395 rows, and
 * the web-app performance guideline requires virtualizing lists > 100 items.
 * At or below it, every row renders in normal flow (no scroll cap), which
 * also keeps small tables and jsdom tests on the simple path.
 */
const VIRTUALIZATION_THRESHOLD = 100;
/** Estimated collapsed-row height; corrected by dynamic measurement. */
const MAIN_ROW_ESTIMATE_PX = 44;
/** Estimated expanded-detail height; corrected by dynamic measurement. */
const DETAIL_ROW_ESTIMATE_PX = 160;
/** Scroll viewport for the virtualized table (no theme token exists for viewport-relative heights). */
/**
 * Flex-basis for the virtualized scroll body. In a height-constrained parent
 * the container grows/shrinks to fill exactly (flex 1 1 + minHeight 0); in an
 * unconstrained page flow this basis is the fallback height, so the table
 * still gets a bounded scroll container (virtualization needs one).
 */
const VIRTUAL_FALLBACK_HEIGHT = '70vh';

/** First-click direction per column; a second click on the column flips it. */
const DEFAULT_DIRECTIONS: Record<CatalogSortKey, 'asc' | 'desc'> = {
  name: 'asc',
  usage: 'desc',
  lastUsedAt: 'desc',
};

/**
 * Deterministic DOM id for a row's detail region — lets the expand toggle
 * reference it via aria-controls. Row ids are sanitized to id-safe chars.
 */
const detailRowId = (rowId: string): string =>
  `catalog-detail-${rowId.replace(/[^A-Za-z0-9_-]/g, '_')}`;

const scopeHintLabel = (
  scope: CatalogRow['linkedScope'],
  labels: CatalogLabels
): string | null => {
  switch (scope) {
    case 'shared':
      return labels.sharedScopeLabel;
    case 'personal':
      return labels.personalScopeLabel;
    case 'public':
      return labels.publicScopeLabel;
    default:
      return null;
  }
};

/** Chip color for a linked-resource scope — mirrors the scope column. */
const scopeChipColor = (
  scope: CatalogRow['linkedScope']
): 'primary' | 'info' | 'default' => {
  if (scope === 'shared') return 'primary';
  if (scope === 'public') return 'info';
  return 'default';
};

interface CatalogMainRowProps {
  row: CatalogRow;
  labels: CatalogLabels;
  showLinked: boolean;
  /** Whether the expand-chevron column is present (renderDetail provided). */
  showDetailColumn: boolean;
  selected: boolean;
  expanded: boolean;
  pending: boolean;
  onToggle: () => void;
  onToggleExpand: () => void;
  onAction: () => void;
  /** Set by the virtualized body for dynamic row measurement. */
  rowRef?: React.Ref<HTMLTableRowElement>;
  /** Virtual item index — rendered as data-index for the measurer. */
  virtualIndex?: number;
}

const CatalogMainRow: React.FC<CatalogMainRowProps> = ({
  row,
  labels,
  showLinked,
  showDetailColumn,
  selected,
  expanded,
  pending,
  onToggle,
  onToggleExpand,
  onAction,
  rowRef,
  virtualIndex,
}) => {
  const linkedHint = scopeHintLabel(row.linkedScope, labels);
  const measureProps =
    virtualIndex === undefined ? {} : { 'data-index': virtualIndex };

  return (
    <TableRow hover selected={selected} ref={rowRef} {...measureProps}>
      <TableCell padding="checkbox">
        <Checkbox
          checked={selected}
          onChange={onToggle}
          slotProps={{ input: { 'aria-label': `Select ${row.name}` } }}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Box
            component="span"
            sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: row.color ?? 'action.disabledBackground',
            }}
          />
          <Typography variant="body2">{row.name}</Typography>
          {row.archived && <Chip label="Archived" size="xSmall" />}
        </Stack>
      </TableCell>
      <TableCell>
        {row.scope === 'shared' ? (
          <Chip label={labels.sharedScopeLabel} color="primary" size="xSmall" />
        ) : (
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Chip label={labels.personalScopeLabel} size="xSmall" />
            {row.ownerLabel !== null && (
              <Typography variant="caption" color="text.secondary">
                {row.ownerLabel}
              </Typography>
            )}
          </Stack>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2">{row.usage}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          {formatLastUsed(row.lastUsedAt)}
        </Typography>
      </TableCell>
      {showLinked && (
        <TableCell>
          {row.linkedLabel === null ? (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {linkedHint !== null && (
                <Chip
                  label={linkedHint}
                  color={scopeChipColor(row.linkedScope)}
                  size="xSmall"
                />
              )}
              <Typography variant="body2">{row.linkedLabel}</Typography>
            </Stack>
          )}
        </TableCell>
      )}
      {showDetailColumn && (
        <TableCell padding="checkbox">
          <Tooltip
            title={
              expanded
                ? (labels.collapseDetailTooltip ?? 'Hide details')
                : (labels.expandDetailTooltip ?? 'Show details')
            }
          >
            <IconButton
              size="small"
              aria-label={
                expanded
                  ? `Collapse ${row.name} details`
                  : `Expand ${row.name} details`
              }
              aria-expanded={expanded}
              aria-controls={expanded ? detailRowId(row.id) : undefined}
              onClick={onToggleExpand}
            >
              {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Tooltip>
        </TableCell>
      )}
      <TableCell align="right" padding="checkbox">
        <Tooltip title={row.archived ? 'Restore' : 'Archive'}>
          {/* span keeps the tooltip alive while the button is disabled */}
          <span>
            <IconButton
              size="small"
              aria-label={
                row.archived ? `Restore ${row.name}` : `Archive ${row.name}`
              }
              disabled={pending}
              onClick={onAction}
            >
              {row.archived ? (
                <UnarchiveOutlinedIcon fontSize="small" />
              ) : (
                <ArchiveOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

CatalogMainRow.displayName = 'CatalogMainRow';

interface CatalogDetailRowProps {
  row: CatalogRow;
  columnCount: number;
  renderDetail: (row: CatalogRow) => ReactNode;
  rowRef?: React.Ref<HTMLTableRowElement>;
  virtualIndex?: number;
}

const CatalogDetailRow: React.FC<CatalogDetailRowProps> = ({
  row,
  columnCount,
  renderDetail,
  rowRef,
  virtualIndex,
}) => {
  const measureProps =
    virtualIndex === undefined ? {} : { 'data-index': virtualIndex };

  return (
    <TableRow ref={rowRef} id={detailRowId(row.id)} {...measureProps}>
      <TableCell colSpan={columnCount} sx={{ bgcolor: 'action.hover' }}>
        {renderDetail(row)}
      </TableCell>
    </TableRow>
  );
};

CatalogDetailRow.displayName = 'CatalogDetailRow';

const SkeletonRow: React.FC<{ columnCount: number }> = ({ columnCount }) => (
  <TableRow>
    <TableCell padding="checkbox">
      <Skeleton variant="rounded" width={18} height={18} sx={{ mx: 'auto' }} />
    </TableCell>
    {Array.from({ length: columnCount - 1 }, (_, index) => (
      <TableCell key={index}>
        <Skeleton variant="text" />
      </TableCell>
    ))}
  </TableRow>
);

SkeletonRow.displayName = 'SkeletonRow';

/** Zero-height-capable filler that keeps virtualized rows at true offsets. */
const SpacerRow: React.FC<{ height: number; columnCount: number }> = ({
  height,
  columnCount,
}) => (
  <TableRow aria-hidden sx={{ height: `${height}px` }}>
    <TableCell colSpan={columnCount} sx={{ p: 0, border: 0 }} />
  </TableRow>
);

SpacerRow.displayName = 'SpacerRow';

/**
 * One virtualized item: an expanded row contributes two entries (main +
 * detail), each its own <tr>, so both can be dynamically measured.
 */
interface VirtualEntry {
  key: string;
  row: CatalogRow;
  kind: 'main' | 'detail';
}

const buildVirtualEntries = (
  rows: CatalogRow[],
  expandedIds: Record<string, boolean>,
  hasDetail: boolean
): VirtualEntry[] => {
  const entries: VirtualEntry[] = [];
  for (const row of rows) {
    entries.push({ key: row.id, row, kind: 'main' });
    if (hasDetail && (expandedIds[row.id] ?? false)) {
      entries.push({ key: `${row.id}:detail`, row, kind: 'detail' });
    }
  }
  return entries;
};

interface CatalogVirtualBodyProps {
  /** Flattened virtual entries — see buildVirtualEntries. */
  entries: VirtualEntry[];
  /**
   * Owned by CatalogTable, NOT this component: useVirtualizer must live in
   * the component that renders the scroll container, otherwise its layout
   * effect runs before the ancestor container ref attaches and the
   * virtualizer never sees a scroll element (renders zero rows).
   */
  virtualizer: Virtualizer<HTMLDivElement, HTMLTableRowElement>;
  labels: CatalogLabels;
  columnCount: number;
  showLinked: boolean;
  selectedSet: Set<string>;
  expandedIds: Record<string, boolean>;
  pending: boolean;
  onToggleRow: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onRowAction: (row: CatalogRow) => void;
  renderDetail?: (row: CatalogRow) => ReactNode;
}

/**
 * Windowed table body (TanStack Virtual). Uses the spacer-row technique so
 * <tr> elements stay in normal table flow (column alignment intact), with
 * per-row dynamic measurement via data-index + measureElement — expanded
 * detail rows are separate entries so their real height is measured too.
 */
const CatalogVirtualBody: React.FC<CatalogVirtualBodyProps> = ({
  entries,
  virtualizer,
  labels,
  columnCount,
  showLinked,
  selectedSet,
  expandedIds,
  pending,
  onToggleRow,
  onToggleExpand,
  onRowAction,
  renderDetail,
}) => {
  const items = virtualizer.getVirtualItems();
  const paddingTop = items.length > 0 ? items[0].start : 0;
  const paddingBottom =
    items.length > 0
      ? virtualizer.getTotalSize() - items[items.length - 1].end
      : 0;

  return (
    <TableBody>
      {paddingTop > 0 && (
        <SpacerRow height={paddingTop} columnCount={columnCount} />
      )}
      {items.map((item) => {
        const entry = entries[item.index];
        if (entry.kind === 'detail' && renderDetail) {
          return (
            <CatalogDetailRow
              key={entry.key}
              row={entry.row}
              columnCount={columnCount}
              renderDetail={renderDetail}
              rowRef={virtualizer.measureElement}
              virtualIndex={item.index}
            />
          );
        }
        return (
          <CatalogMainRow
            key={entry.key}
            row={entry.row}
            labels={labels}
            showLinked={showLinked}
            showDetailColumn={renderDetail !== undefined}
            selected={selectedSet.has(entry.row.id)}
            expanded={expandedIds[entry.row.id] ?? false}
            pending={pending}
            onToggle={(): void => onToggleRow(entry.row.id)}
            onToggleExpand={(): void => onToggleExpand(entry.row.id)}
            onAction={(): void => onRowAction(entry.row)}
            rowRef={virtualizer.measureElement}
            virtualIndex={item.index}
          />
        );
      })}
      {paddingBottom > 0 && (
        <SpacerRow height={paddingBottom} columnCount={columnCount} />
      )}
    </TableBody>
  );
};

CatalogVirtualBody.displayName = 'CatalogVirtualBody';

/**
 * Plain MUI table (not DataGrid — expandable detail rows need full control).
 * Selection, sorting, and actions are controlled; expansion is local UI state
 * (surfaced via onExpansionChange for tracking). Bodies over
 * VIRTUALIZATION_THRESHOLD rows are windowed — see CatalogVirtualBody.
 */
export const CatalogTable: React.FC<CatalogTableProps> = ({
  rows,
  labels,
  sort,
  onSortChange,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onRowAction,
  pending,
  loading,
  renderDetail,
  emptyState,
  onExpansionChange,
}) => {
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const showLinked = labels.linkedColumnHeader !== null;
  const columnCount =
    5 + (showLinked ? 1 : 0) + (renderDetail ? 1 : 0) + 1;

  const virtualize = !loading && rows.length > VIRTUALIZATION_THRESHOLD;

  const entries = useMemo(
    () =>
      virtualize
        ? buildVirtualEntries(rows, expandedIds, renderDetail !== undefined)
        : [],
    [virtualize, rows, expandedIds, renderDetail]
  );

  // Must live here (the component that renders the scroll container) so the
  // container ref is attached before the virtualizer's layout effect runs.
  const virtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: entries.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) =>
      entries[index].kind === 'detail'
        ? DETAIL_ROW_ESTIMATE_PX
        : MAIN_ROW_ESTIMATE_PX,
    getItemKey: (index) => entries[index].key,
    overscan: 10,
    enabled: virtualize,
  });

  const selectedSet = new Set(selectedIds);
  const selectedVisibleCount = rows.filter((row) =>
    selectedSet.has(row.id)
  ).length;
  const allSelected = rows.length > 0 && selectedVisibleCount === rows.length;
  const someSelected = selectedVisibleCount > 0 && !allSelected;

  const handleSort = (key: CatalogSortKey): void => {
    if (sort.key === key) {
      onSortChange({
        key,
        direction: sort.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      onSortChange({ key, direction: DEFAULT_DIRECTIONS[key] });
    }
  };

  const toggleExpand = (id: string): void => {
    const expanded = !(expandedIds[id] ?? false);
    setExpandedIds((prev) => ({ ...prev, [id]: expanded }));
    onExpansionChange?.(id, expanded);
  };

  const sortHeader = (key: CatalogSortKey, label: string): ReactNode => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : DEFAULT_DIRECTIONS[key]}
      onClick={(): void => handleSort(key)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <TableContainer
      ref={scrollRef}
      sx={
        virtualize
          ? {
              flex: `1 1 ${VIRTUAL_FALLBACK_HEIGHT}`,
              minHeight: 0,
              overflow: 'auto',
            }
          : undefined
      }
    >
      <Table
        size="small"
        stickyHeader={virtualize}
        aria-label={`${labels.nounPlural} catalog`}
      >
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                disabled={loading || rows.length === 0}
                onChange={onToggleAll}
                slotProps={{
                  input: { 'aria-label': 'Select all visible rows' },
                }}
              />
            </TableCell>
            <TableCell>{sortHeader('name', 'Name')}</TableCell>
            <TableCell>Scope</TableCell>
            <TableCell align="right">{sortHeader('usage', 'Usage')}</TableCell>
            <TableCell>{sortHeader('lastUsedAt', 'Last used')}</TableCell>
            {showLinked && <TableCell>{labels.linkedColumnHeader}</TableCell>}
            {renderDetail && <TableCell padding="checkbox" />}
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>
        {virtualize ? (
          <CatalogVirtualBody
            entries={entries}
            virtualizer={virtualizer}
            labels={labels}
            columnCount={columnCount}
            showLinked={showLinked}
            selectedSet={selectedSet}
            expandedIds={expandedIds}
            pending={pending}
            onToggleRow={onToggleRow}
            onToggleExpand={toggleExpand}
            onRowAction={onRowAction}
            renderDetail={renderDetail}
          />
        ) : (
          <TableBody>
            {loading &&
              Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
                <SkeletonRow key={index} columnCount={columnCount} />
              ))}
            {!loading && rows.length === 0 && emptyState !== undefined && (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ border: 0 }}>
                  {emptyState}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => {
                const expanded = expandedIds[row.id] ?? false;
                return (
                  <React.Fragment key={row.id}>
                    <CatalogMainRow
                      row={row}
                      labels={labels}
                      showLinked={showLinked}
                      showDetailColumn={renderDetail !== undefined}
                      selected={selectedSet.has(row.id)}
                      expanded={expanded}
                      pending={pending}
                      onToggle={(): void => onToggleRow(row.id)}
                      onToggleExpand={(): void => toggleExpand(row.id)}
                      onAction={(): void => onRowAction(row)}
                    />
                    {expanded && renderDetail && (
                      <CatalogDetailRow
                        row={row}
                        columnCount={columnCount}
                        renderDetail={renderDetail}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
};

CatalogTable.displayName = 'CatalogTable';
